import { Permissions } from '../../../../../core/constants/permissions';
import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TrialDetail, TrialSupportContact } from 'app/core/models/trial-detail.model';
import { TrialService } from 'app/core/services/trial.service';
import { Countries } from 'app/core/constants/countries';
import { LogHelper } from 'app/core/helpers/log.helper';
import { AuthService } from 'app/core/services/auth.service';
import { AlertService } from 'app/shared/alert/alert.service';
import { Cultures } from 'app/core/constants/cultures';
import { GlobalConstants } from 'app/core/constants/global-constants';

@Component({
  selector: 'app-trial-support-contacts',
  templateUrl: './trial-support-contacts.component.html',
  styleUrls: ['./trial-support-contacts.component.scss']
})
export class TrialSupportContactsComponent implements OnInit, AfterViewInit {
  @Input('trial') trial: TrialDetail;

  @Output() hasChanged = new EventEmitter<boolean>(false);

  cultures: { text: string, value: string }[] = [];
  countryList: { text: string, value: string }[] = [];
  isSaving = false;
  hasChanges = false;
  removedContacts: string[] = [];
  defaultContactForm: UntypedFormGroup;
  defaultContactsFormProcessing = false;
  Permissions = Permissions;

  constructor(public authService: AuthService, private readonly alertService: AlertService, private readonly trialService: TrialService) { }

  ngOnInit() {
    // Populate cultures
    this.cultures.push({ value: '', text: 'All' });
    for (const culture of Cultures.all()) {
      this.cultures.push({ value: culture.culture, text: culture.name });
    }

    // Populate countries
    for (const country of Countries.all()) {
      this.countryList.push({ value: country.code, text: country.name });
    }

    this.defaultContactForm = new UntypedFormGroup({
      supportTextHiddenInApp: new UntypedFormControl(false),
      supportEmailHiddenInApp: new UntypedFormControl(false),
      supportEmail: new UntypedFormControl({
        value: '',
        disabled: !this.authService.hasPermission(Permissions.TrialSupportContactsAdd)
      }, [Validators.required, Validators.pattern(GlobalConstants.emailValidatorPattern)]),
      supportPhone: new UntypedFormControl({
        value: '',
        disabled: !this.authService.hasPermission(Permissions.TrialSupportContactsAdd)
      }, [Validators.required, Validators.pattern(GlobalConstants.telephoneNumberPattern)])
    });
  }
  ngAfterViewInit() {
    if (this.trial !== undefined && this.trial !== null) {
      Promise.resolve().then(() => {
        this.defaultContactForm.patchValue({
          supportEmailHiddenInApp: this.trial.supportEmailHiddenInApp,
          supportTextHiddenInApp: !this.trial.supportTextVisibleInApp,
          supportEmail: this.trial.supportEmail,
          supportPhone: this.trial.supportPhone
        });
      });
    }
  }

  isValidPatientGoEmail(email: string): boolean {
    const pattern = /^[a-zA-Z0-9._%+-]+@patientgo\.com$/;
    return pattern.test(email);
  }

  onRemoveContact(index: number) {
    this.removedContacts.push(this.trial.supportContacts[index].id);
    this.trial.supportContacts.splice(index, 1);
    this.hasChanges = true;
    this.hasChanged.emit(this.hasChanges);
  }

  onUpdateCountry(index: number, value: string) {
    if (this.trial.supportContacts[index].country === value)
      return;

    this.trial.supportContacts[index].country = value;
    this.hasChanges = true;
    this.hasChanged.emit(this.hasChanges);
  }

  onUpdateCulture(index: number, value: string) {
    this.trial.supportContacts[index].culture = value;
    this.hasChanges = true;
    this.hasChanged.emit(this.hasChanges);
  }

  onUpdateLabel(index: number, value: string) {
    this.trial.supportContacts[index].label = value;
    this.hasChanges = true;
    this.hasChanged.emit(this.hasChanges);
  }

  onUpdateContact(index: number, value: string) {
    this.trial.supportContacts[index].contact = value;
    this.hasChanges = true;
    this.hasChanged.emit(this.hasChanges);
  }

  onAddContact() {
    this.trial.supportContacts.push(new TrialSupportContact(null, '', '', 'AF', ''));
  }

  onUpdateSupportContacts() {
    let isValid = true;
    this.trial.supportContacts.forEach(contact => {
      if (!contact.label.trim()) {
        // As of 1.28 the support contact 'label' field is optional in the UI, but the database still requires it.
        // For now, we set it to a single space if empty so that no app updates are required
        contact.label = ' ';
      }

      if (contact.contact === null || contact.contact === '') {
        isValid = false;
      }
    });

    if (isValid) {
      this.isSaving = true;
      this.trialService.updateContacts(
        this.trial.id,
        this.removedContacts,
        this.trial.supportContacts).subscribe({
          next: () => {
            this.alertService.showSuccessAlert('Support contacts have been updated.');
            this.removedContacts = [];
            this.hasChanges = false;
            this.isSaving = false;
            this.hasChanged.emit(this.hasChanges);

            // Update trial to re-load contact Id's
            this.trialService.retrieveTrial(this.trial.id).subscribe(trial => {
              this.trial = trial;
            });
          },
          error: error => {
            LogHelper.log(error);
            this.isSaving = false;
            this.alertService.showWarningAlert(error.error.title);
          }
        });
    } else {
      this.alertService.showWarningAlert('Please provide a contact number for ALL support contacts above.');
    }
  }

  onUpdateDefaultSupportContacts(): void {
    const form = this.defaultContactForm;

    if (form.valid) {
      this.defaultContactsFormProcessing = true;
      this.trialService.updateDefaultSupportContact(
        this.trial.id,
        form.get('supportEmailHiddenInApp').value,
        !form.get('supportTextHiddenInApp').value, // Need to reverse the value
        form.get('supportEmail').value,
        form.get('supportPhone').value).subscribe({
          next: () => {
            this.trial.supportEmailHiddenInApp = form.get('supportEmailHiddenInApp').value;
            this.trial.supportPhone = form.get('supportPhone').value;
            this.trial.supportEmail = form.get('supportEmail').value;
            this.defaultContactForm.markAsUntouched();
            this.defaultContactsFormProcessing = false;

            this.alertService.showSuccessAlert('Default support contacts updated successfully');
          },
          error: () => {
            this.alertService.showWarningAlert('Sorry, there was a problem.');
            this.defaultContactsFormProcessing = false;
          }
        });
    }
  }
}
